import React from "react";
import {
    PublishProvidersEnum,
    SocialConnectRestControllerApiFactory,
    SocialPost,
    SocialPostRestControllerApiFactory,
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {Grid} from "@mui/material";
import {FormModel, useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {types} from "../../../../components/form/types";
import {FormButtonProps} from "../../../../components/form/form_inputs/form_button";

export const SocialPostEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(SocialPostRestControllerApiFactory);

    const form: FormModel<SocialPost> = useForm({
        fetcher: (id) => ctrl.getSocialPost(id),
        updater: (bean) => ctrl.saveSocialPost(bean),
        remover: (id) => ctrl.deleteOrDismissSocialPost(id),
        factory: () => ({} as SocialPost),
        extraActions: [
            {
                label: "Publicar en redes",
                disabled: () => !form.propModel.value?.id,
                action: () => doPublish(
                    [PublishProvidersEnum.FACEBOOK, PublishProvidersEnum.INSTAGRAM, PublishProvidersEnum.LINKEDIN]),
            } as FormButtonProps,
            {
                label: "LinkedIn (empresa)",
                disabled: () => !form.propModel.value?.id,
                action: () => doPublish([PublishProvidersEnum.LINKEDIN], false),
            } as FormButtonProps,
            {
                label: "LinkedIn (perfil)",
                disabled: () => !form.propModel.value?.id,
                action: () => doPublish([PublishProvidersEnum.LINKEDIN], true),
            } as FormButtonProps,
        ],
    } as UseFormProps<SocialPost>);

    // linkedinAsMember: con la página y el perfil conectados a la vez, elige a cuál publicar
    // (los tokens de LinkedIn no son intercambiables). undefined = la única cuenta que haya.
    const doPublish = (providers: PublishProvidersEnum[], linkedinAsMember?: boolean) => {
        const actions = apiManager.factory(SocialConnectRestControllerApiFactory);
        apiManager.execute(actions.publish(form.getIdOrErr(), providers, linkedinAsMember), "Publicación lanzada")
            .then((pubs) => {
                const ok = (pubs ?? []).filter((p) => p.status === "PUBLISHED").length;
                const fail = (pubs ?? []).length - ok;
                apiManager.enqueueSnackbar(`Publicado en ${ok} red(es), ${fail} fallo(s)`,
                    {variant: fail === 0 ? "success" : "warning"});
                apiManager.executeRaw(ctrl.getSocialPost(form.getIdOrErr()))
                    .then((r) => form.setter(r.data));
            });
    };

    return (
        <FormContent form={form} title={"Post social"}>
            <FormInput field={"text"} label={"Texto del post (se usa en la red que no tenga copy propio)"} rows={8}/>

            {/* Copy por red. Vacío = se publica el texto de arriba. Antes, tener copys
                distintos obligaba a crear un SocialPost por red y la pieza quedaba partida
                en tres registros sin relación. */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"textLinkedin"} rows={6}
                               label={"Copy LinkedIn (vacío = el de arriba)"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"textFacebook"} rows={6}
                               label={"Copy Facebook (vacío = el de arriba)"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"textInstagram"} rows={6}
                               label={"Copy Instagram (vacío = el de arriba)"}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={8}>
                    <FormInput field={"imageUrl"} label={"URL de imagen (obligatoria para Instagram)"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormSelect field={"status"} label={"Estado"} type={types.socialPostStatuses}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"sourceUrl"} label={"URL de la noticia (fuente)"}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"sourceTitle"} label={"Titular de la fuente"}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"ctaUrl"} label={"CTA: enlace (irá como 1er comentario en LinkedIn, con UTM)"}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"ctaComment"} label={"CTA: texto del comentario (vacío = frase por defecto)"}/>
                </Grid>
            </Grid>

            {/* El repost del perfil de Ramón sobre la publicación de la página. El perfil tiene
                más seguidores que la página, así que el repost es la superficie que ve la mayoría
                de la audiencia: vacío = esa mayoría lee un copy escrito para la marca. Va por
                pieza y no por configuración global — una frase única para todos los posts es
                justo lo que dejó vacía la propiedad del despliegue. */}
            <FormInput field={"reshareCommentary"} rows={3}
                       label={"Repost del perfil: texto propio (vacío = repost sin comentario)"}/>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"angle"} label={"Ángulo / tesis"}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"scheduledAt"} label={"Programar para"} type="datetime"/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"segment"} label={"Segmento"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"abVariant"} label={"Variante A/B"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"productOffered"} label={"Producto/servicio"}/>
                </Grid>
            </Grid>

            <FormInput field={"imagePrompt"} label={"Brief de imagen (DALL·E)"} rows={2}/>
            <FormInput field={"observation"} label={"Observaciones"} rows={2}/>
        </FormContent>
    );
};
